/**
 * <ScrollHint /> — dica de rolagem da LP Sofia.
 *
 * Aparece quando a tela fica PARADA por >2s (usuário pode não perceber que a
 * página rola). Some ao rolar/tocar e quando o modal de agendamento abre.
 * Mostra só na área inicial (primeira ~meia tela), pra não conflitar com o
 * botão flutuante e porque é onde a pessoa trava sem saber que há mais abaixo.
 *
 * Visual: minimalista — dois chevrons finos dourados descendo suave (estilo
 * indicador de scroll de site de luxo). Sem caixa, sem texto, baixa opacidade.
 */
import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const IDLE_MS = 2000;

const bob = keyframes`
  0%   { opacity: 0;   transform: translateY(-5px); }
  45%  { opacity: 0.7; }
  100% { opacity: 0;   transform: translateY(7px); }
`;

const Wrap = styled(motion.div)`
  position: fixed;
  bottom: 28px;
  left: 50%;
  margin-left: -13px;
  z-index: 40;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: center;

  svg {
    display: block;
    filter: drop-shadow(0 1px 6px rgba(0, 0, 0, 0.45));
    animation: ${bob} 1.9s cubic-bezier(0.5, 0, 0.5, 1) infinite;
  }
  svg + svg {
    margin-top: -5px;
    animation-delay: 0.18s;
  }

  @media (prefers-reduced-motion: reduce) {
    svg { animation: none; opacity: 0.55; }
  }
`;

const Chevron = () => (
  <svg width="26" height="11" viewBox="0 0 26 11" fill="none" aria-hidden="true">
    <path
      d="M2 2L13 9L24 2"
      stroke="#e7cf9c"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default function ScrollHint() {
  const [show, setShow] = useState(false);
  const [raised, setRaised] = useState(false); // sobe acima do botão flutuante
  const timer = useRef(null);

  useEffect(() => {
    // mostra em QUALQUER etapa (guia de navegação contínua), desde que ainda
    // haja conteúdo relevante abaixo. Some perto do fim (nada mais pra rolar).
    const temConteudoAbaixo = () => {
      const vh = window.innerHeight;
      const scrolled = window.scrollY;
      const docH = document.documentElement.scrollHeight;
      return docH - (scrolled + vh) > vh * 0.4;
    };
    // o modal de agendamento trava o scroll via body.style.overflowY = 'hidden'
    const modalAberto = () => document.body.style.overflowY === 'hidden';

    const arm = () => {
      clearTimeout(timer.current);
      setShow(false);
      timer.current = setTimeout(() => {
        if (temConteudoAbaixo() && !modalAberto()) {
          // o botão flutuante "Quero mais informações" liga em scrollY > 90;
          // quando ele está visível, o chevron sobe pra ficar ACIMA dele.
          setRaised(window.scrollY > 90);
          setShow(true);
        }
      }, IDLE_MS);
    };

    arm();
    window.addEventListener('scroll', arm, { passive: true });
    window.addEventListener('touchmove', arm, { passive: true });
    window.addEventListener('wheel', arm, { passive: true });
    return () => {
      clearTimeout(timer.current);
      window.removeEventListener('scroll', arm);
      window.removeEventListener('touchmove', arm);
      window.removeEventListener('wheel', arm);
    };
  }, []);

  return (
    <AnimatePresence>
      {show && (
        <Wrap
          key="sofia-scroll-hint"
          style={{ bottom: raised ? 96 : 28 }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          <Chevron />
          <Chevron />
        </Wrap>
      )}
    </AnimatePresence>
  );
}
